import { assertNever, defined, definedMap } from "@glideapps/ts-necessities";
import { asMaybeDate, asMaybeString } from "common/computation-model/data";
import type { WireContainerComponent } from "common/generator/components/fluent-components-containers";
import { ArrayScreenFormat } from "common/generator/description";
import { anyDefined } from "common/Support";
import type { WireComponent, WireScreen } from "common/wire";
import type { WireSignInComponent } from "common/wire/base-components";
import { WireComponentKind } from "common/wire/component-kind";
import { ButtonBarBackground, ButtonBarSize, ContainerStyle, UIAlignment } from "common/wire/types";
import { useWireAppTheme } from "hooks/use-wireapp-theme";
import * as React from "react";
import { TailwindThemeProvider } from "sharedUI/lib/tailwind-theme-provider";
import { mergeTheme } from "sharedUI/lib/theme-utils";
import "twin.macro";
import AuthController from "webapp/chrome/smart/auth-controller/auth-controller";
import { AuthControllerPropsContextHack } from "webapp/glide-app-player";
import type { WireBackend } from "webapp/hydrated-ui/types";
import { defaultWireAppAccentContextOverlay } from "webapp/lib/theme/core-themes/wireapp-theme";
import { styled } from "webapp/styled-components";
import { CardCollectionRenderer } from "webapp/ui/smart/card-collection/card-collection";
import DateTimePicker from "webapp/ui/smart/date-time-picker/date-time-picker";
import { Breadcrumbs } from "webapp/ui/views/breadcrumbs/breadcrumbs";
import { DataGrid } from "webapp/ui/views/data-grid/data-grid";
import { Fields } from "webapp/ui/views/fields/fields";
import { FormSection } from "webapp/ui/views/form-section/form-section";
import { WireButtonBar } from "webapp/ui/views/wire-button-bar/wire-button-bar";
import { WireChoice } from "webapp/ui/views/wire-choice/wire-choice";
import {
    heroStyleToSectionStyle,
    SectionStyle,
    SectionStyleProvider,
    WireContainer,
} from "webapp/ui/views/wire-container/wire-container";
import { WireField } from "webapp/ui/views/wire-field/wire-field";
import { WireFilePicker, WireImagePicker } from "webapp/ui/views/wire-file-picker/wire-file-picker";
import { WireHero } from "webapp/ui/views/wire-hero/wire-hero";
import { WireHint } from "webapp/ui/views/wire-hint/wire-hint";
import { WireImage } from "webapp/ui/views/wire-image/wire-image";
import { WireRichText } from "webapp/ui/views/wire-rich-text/wire-rich-text";
import { Separator } from "webapp/ui/views/wire-separator/wire-separator";
import { WireSwitch } from "webapp/ui/views/wire-switch/wire-switch";
import { WireVideo } from "webapp/ui/views/wire-video/wire-video";
import type { WireRenderer } from "./wire-renderer";

interface Props {
    readonly currentScreen: WireScreen;
    readonly backend: WireBackend;
}

interface WireComponentRendererProps {
    readonly component: WireComponent;
    readonly backend: WireBackend;
}

const WireComponentRenderer: React.VFC<WireComponentRendererProps> = p => {
    const { component, backend } = p;
    const { appKind } = backend;

    if (component.kind === WireComponentKind.Button) {
        if (component.action === undefined) return null;
        return (
            <WireContainer>
                <WireButtonBar
                    overrideAppearance={component.appearance}
                    alignment={UIAlignment.Center}
                    background={ButtonBarBackground.None}
                    size={ButtonBarSize.Wide}
                    // title={component.description}
                    buttons={[component]}
                    backend={backend}
                />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.TextField) {
        return (
            <WireContainer>
                <WireField
                    placeholder={component.placeholder}
                    dataType="text"
                    isEnabled={component.value.onChangeToken !== undefined}
                    error={component.value.error}
                    value={component.value.value}
                    onChange={newVal => backend.valueChanged(defined(component.value.onChangeToken), newVal)}
                    isRequired={component.isRequired}
                    maximumChars={undefined}
                    title={component.title}
                />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.NumberField) {
        return (
            <WireContainer>
                <WireField
                    placeholder={component.placeholder}
                    dataType="number"
                    isEnabled={component.value.onChangeToken !== undefined}
                    error={component.value.error}
                    value={asMaybeString(component.value.value) ?? ""}
                    onChange={newVal => backend.valueChanged(defined(component.value.onChangeToken), newVal)}
                    isRequired={component.isRequired}
                    maximumChars={undefined}
                    title={component.title}
                />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.PhoneField) {
        return (
            <WireContainer>
                <WireField
                    placeholder={component.placeholder}
                    dataType="phone"
                    isEnabled={component.value.onChangeToken !== undefined}
                    error={component.value.error}
                    value={asMaybeString(component.value.value) ?? ""}
                    onChange={newVal => backend.valueChanged(defined(component.value.onChangeToken), newVal)}
                    isRequired={component.isRequired}
                    maximumChars={undefined}
                    title={component.title}
                />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.EmailField) {
        return (
            <WireContainer>
                <WireField
                    placeholder={component.placeholder}
                    dataType="email"
                    isEnabled={component.value.onChangeToken !== undefined}
                    error={component.value.error}
                    value={asMaybeString(component.value.value) ?? ""}
                    onChange={newVal => backend.valueChanged(defined(component.value.onChangeToken), newVal)}
                    isRequired={component.isRequired}
                    maximumChars={undefined}
                    title={component.title}
                />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.DateTimeField) {
        return (
            <WireContainer>
                <DateTimePicker
                    variant={component.withTime ? "date-time" : "date"}
                    title={component.title}
                    placeholder={component.placeholder}
                    value={asMaybeDate(component.value.value)}
                    pastAndOrFuture={component.pastOrFuture}
                    isEnabled={component.value.onChangeToken !== undefined}
                    onChange={newVal => backend.valueChanged(defined(component.value.onChangeToken), newVal)}
                    isRequired={component.isRequired}
                    appKind={appKind}
                />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.ImagePicker) {
        return (
            <WireContainer>
                <WireImagePicker {...component} backend={backend} />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.FilePicker) {
        return (
            <WireContainer>
                <WireFilePicker {...component} backend={backend} />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.Switch) {
        return (
            <WireContainer>
                <WireSwitch {...component} backend={backend} />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.List && component.format === ArrayScreenFormat.CardCollection) {
        return (
            <WireContainer>
                <CardCollectionRenderer {...component} backend={backend} />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.List && component.format === ArrayScreenFormat.Choice) {
        return (
            <WireContainer>
                <WireChoice {...component} backend={backend} />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.Hero) {
        const { bgStyle, withBlur, backgroundImage, backgroundEffect } = component;
        return (
            <WireContainer
                backgroundImage={backgroundImage ?? undefined}
                background={heroStyleToSectionStyle(bgStyle)}
                blur={withBlur}
                backgroundEffect={backgroundEffect}>
                <WireHero {...component} backend={backend} />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.List && component.format === ArrayScreenFormat.DataGrid) {
        return (
            <WireContainer>
                <DataGrid
                    {...component}
                    onPageChange={v =>
                        component.paging?.pageIndex.onChangeToken &&
                        backend.valueChanged(component.paging.pageIndex.onChangeToken, v)
                    }
                />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.ContactForm) {
        return (
            <WireContainer>
                <FormSection
                    email={component.email?.value}
                    emailError={component.email?.error}
                    message={component.message?.value}
                    messageError={component.message?.error}
                    name={component.name?.value}
                    nameError={component.name?.error}
                    phone={component.phone?.value}
                    phoneError={component.phone?.error}
                    onEmailChange={definedMap(
                        component.email?.onChangeToken,
                        token => x => backend.valueChanged(token, x)
                    )}
                    onMessageChange={definedMap(
                        component.message?.onChangeToken,
                        token => x => backend.valueChanged(token, x)
                    )}
                    onNameChange={definedMap(
                        component.name?.onChangeToken,
                        token => x => backend.valueChanged(token, x)
                    )}
                    onPhoneChange={definedMap(
                        component.phone?.onChangeToken,
                        token => x => backend.valueChanged(token, x)
                    )}
                    onSubmit={definedMap(component.submitAction?.token, token => () => backend.runAction(token, false))}
                />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.DynamicButtonBar) {
        if (!component.buttons.some(a => a.action !== null && a.action !== undefined)) return null;
        return (
            <WireContainer>
                <WireButtonBar {...component} backend={backend} />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.Breadcrumbs) {
        return (
            <WireContainer>
                <Breadcrumbs {...component} backend={backend} />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.Container) {
        return <Container {...component} backend={backend} />;
    } else if (component.kind === WireComponentKind.Fields) {
        return (
            <WireContainer>
                <Fields {...component} backend={backend} />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.Hint) {
        if (!anyDefined(component.action, component.actionTitle, component.description)) return null;
        return (
            <WireContainer>
                <WireHint {...component} backend={backend} />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.Separator) {
        return (
            <WireContainer>
                <Separator {...component} backend={backend} />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.RichText) {
        return (
            <WireContainer>
                <WireRichText {...component} backend={backend} />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.Image) {
        return (
            <WireContainer>
                <WireImage {...component} backend={backend} />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.Video) {
        return (
            <WireContainer>
                <WireVideo {...component} backend={backend} />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.SignIn) {
        return (
            <WireContainer>
                <WireAuthController {...component} backend={backend} />
            </WireContainer>
        );
    } else {
        return assertNever(component);
    }
};

const ContainerSectionStyle = styled.section`
    .full-bleed + &.full-bleed {
        margin-top: 0;
    }
`;

const WireAuthController: WireRenderer<WireSignInComponent> = p => {
    const { backend, onFailure, onSuccess } = p;

    const authProps = React.useContext(AuthControllerPropsContextHack);

    const onLoggedIn = React.useCallback(async () => {
        backend.runAction(onSuccess.token, false);
    }, [backend, onSuccess.token]);

    const onFailed = React.useCallback(() => {
        backend.runAction(onFailure.token, false);
    }, [backend, onFailure.token]);

    if (authProps === undefined) return null;

    return (
        <div tw="relative height[500px]">
            <AuthController {...authProps} appTitle={undefined} onLoggedIn={onLoggedIn} onFailed={onFailed} />
        </div>
    );
};

// TODO: This doesn't belong in here, but there's a dependency cycle between
// this and `WireComponentRenderer`
export const Container: WireRenderer<WireContainerComponent> = React.memo(props => {
    const { containerStyle } = props;
    const contents = (
        <div>
            {props.components.map((c, i) => {
                if (c === null) return null;
                return <WireComponentRenderer key={i} component={c} backend={props.backend} />;
            })}
            {containerStyle === ContainerStyle.Default && <div />}
        </div>
    );
    let theme = useWireAppTheme();
    if (containerStyle === ContainerStyle.Default) return <ContainerSectionStyle>{contents}</ContainerSectionStyle>;
    if (containerStyle === ContainerStyle.Accent) {
        theme = mergeTheme(theme, [defaultWireAppAccentContextOverlay]);
        return (
            <SectionStyleProvider value={SectionStyle.Accent}>
                <ContainerSectionStyle className="accent-bg full-bleed" tw="not-first:mt-6">
                    <TailwindThemeProvider theme={theme}>{contents}</TailwindThemeProvider>
                </ContainerSectionStyle>
            </SectionStyleProvider>
        );
    }
    return (
        <ContainerSectionStyle className="full-bleed" tw="bg-bg-front not-first:mt-6">
            {contents}
        </ContainerSectionStyle>
    );
});

export const WireApp: React.VFC<Props> = p => {
    const { currentScreen, backend } = p;

    const content = React.useMemo(() => {
        const items = currentScreen?.components ?? [];
        return items.map((i, index) => {
            if (i === null) return undefined;
            return <WireComponentRenderer key={index} component={i} backend={backend} />;
        });
    }, [backend, currentScreen?.components]);

    return <>{content}</>;
};
